import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const loadRazorpay = () => new Promise((resolve, reject) => {
  if (window.Razorpay) return resolve();
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = resolve;
  s.onerror = () => reject(new Error("Couldn't load the payment window."));
  document.body.appendChild(s);
});

const cancelOrder = (orderId) => orderId && api.post("/payments/cancel", { order_id: orderId }).catch(() => {});

// Razorpay error reasons → customer-friendly text (never show raw provider descriptions or UPI IDs).
const FRIENDLY = {
  payment_cancelled: "You cancelled the payment. No money was taken.",
  payment_timed_out: "The payment timed out. No money was taken — please try again.",
  insufficient_funds: "Your bank declined the payment (insufficient balance). Try another account or card.",
  incorrect_pin: "The UPI PIN was incorrect. Please try again.",
  authentication_failed: "Your bank couldn't confirm the payment. Please try again or use another method.",
};
const friendlyFailure = (err) => FRIENDLY[err?.reason] ||
  "Your payment didn't go through and no money was taken. Please try another UPI app, a card or netbanking.";

export const RazorpayButton = ({ plan, info, busy, setBusy, label }) => {
  const navigate = useNavigate();
  const pay = async () => {
    setBusy(true);
    let orderId = null;
    try {
      await loadRazorpay();
      const { data: o } = await api.post("/payments/razorpay/order", { plan_id: plan.plan_id });
      orderId = o.order_id;
      let settled = false;
      let lastError = null;
      const rz = new window.Razorpay({
        key: o.key_id, order_id: o.order_id, amount: o.amount, currency: o.currency, name: o.name, description: o.description,
        image: `${window.location.origin}/icon-192.png`, prefill: { email: o.email }, theme: { color: "#ec4899" }, retry: { enabled: true, max_count: 3 },
        handler: async (res) => {
          settled = true;
          try {
            const { data } = await api.post("/payments/razorpay/verify", res);
            navigate(`/payment/${data.status === "pending" ? "pending" : "success"}?order=${o.order_id}`);
          } catch (e) {
            navigate(`/payment/failed?order=${o.order_id}&reason=${encodeURIComponent(errMsg(e, "We couldn't confirm the payment yet."))}`);
          }
        },
        modal: {
          ondismiss: () => {
            setBusy(false);
            if (settled) return;
            if (lastError) return navigate(`/payment/failed?order=${orderId}&reason=${encodeURIComponent(friendlyFailure(lastError))}`);
            cancelOrder(orderId);
            navigate(`/payment/cancelled?order=${orderId}`);
          },
        },
      });
      rz.on("payment.failed", (resp) => { lastError = resp?.error || {}; });
      rz.open();
    } catch (e) {
      toast.error(errMsg(e, e.message));
      setBusy(false);
    }
  };
  return (
    <button data-testid={`subscribe-button-${plan.plan_id}`} onClick={pay} disabled={!info.enabled || busy}
      className="w-full h-12 rounded-full brand-gradient text-white font-semibold disabled:opacity-40 transition-opacity hover:opacity-90">
      {info.enabled ? label : "Coming soon"}
    </button>
  );
};
